import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { publicApi } from '../utils/api';
import { Loader2 } from 'lucide-react';

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.href = href;
}

function normalizeSections(rawSections) {
  if (!Array.isArray(rawSections)) return [];

  return rawSections
    .map((section, index) => {
      if (!section || typeof section !== 'object') return null;

      const heading = typeof section.heading === 'string'
        ? section.heading.trim()
        : '';
      const paragraphs = Array.isArray(section.paragraphs)
        ? section.paragraphs
            .filter((paragraph) => typeof paragraph === 'string')
            .map((paragraph) => paragraph.trim())
            .filter(Boolean)
        : [];

      if (!paragraphs.length) return null;

      return {
        heading: heading || `Section ${index + 1}`,
        paragraphs
      };
    })
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    setArticle(null);
    setRelated([]);

    publicApi.getInsight(slug)
      .then(({ article: loadedArticle }) => {
        if (cancelled) return null;

        const sections = normalizeSections(loadedArticle?.sections);
        if (!loadedArticle || !sections.length) {
          throw new Error('This article does not contain publishable content.');
        }

        const normalizedArticle = { ...loadedArticle, sections };
        setArticle(normalizedArticle);
        return publicApi.getInsights(normalizedArticle.category);
      })
      .then((res) => {
        if (!res || cancelled) return;
        setRelated(
          (res.articles || [])
            .filter((item) => item.slug !== slug && item.has_content !== false)
            .slice(0, 3)
        );
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(requestError?.message || 'This insight could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const readingTime = useMemo(() => {
    if (!article) return 1;
    const words = article.sections.reduce((total, section) => (
      total + section.paragraphs.join(' ').trim().split(/\s+/).filter(Boolean).length
    ), 0);
    return Math.max(1, Math.ceil(words / 220));
  }, [article]);

  useEffect(() => {
    if (!article) return undefined;

    const canonicalUrl = `https://tactmark.com/insights/${article.slug}`;
    const metaTitle = article.meta_title || article.title;
    const metaDescription = article.meta_description || '';

    document.title = `${metaTitle} | Tactmark`;
    setMeta('description', metaDescription);
    setMeta('robots', 'index,follow,max-image-preview:large');
    setMeta('og:title', metaTitle, 'property');
    setMeta('og:description', metaDescription, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', metaTitle);
    setMeta('twitter:description', metaDescription);
    setLink('canonical', canonicalUrl);

    let script = document.getElementById('article-schema');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'article-schema';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: article.title,
          description: metaDescription,
          author: { '@type': 'Organization', name: article.author || 'Tactmark Team' },
          datePublished: article.publish_date,
          publisher: { '@type': 'Organization', name: 'Tactmark' },
          mainEntityOfPage: canonicalUrl
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://tactmark.com/' },
            { '@type': 'ListItem', position: 2, name: 'Insights', item: 'https://tactmark.com/insights' },
            { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl }
          ]
        }
      ]
    });

    return () => {
      const currentScript = document.getElementById('article-schema');
      if (currentScript) currentScript.remove();
    };
  }, [article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicNav />
        <main className="text-center py-24" aria-live="polite">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          <p className="text-gray-500 text-sm mt-4">Loading insight…</p>
        </main>
        <PublicFooter />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <PublicNav />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Insights</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy-900 mb-4">This guide is not available.</h1>
          <p className="text-gray-600 leading-relaxed mb-8">
            The page may be unpublished, incomplete, or temporarily unavailable. It has not been left as a blank page.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/insights" className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-2.5 rounded transition-colors">
              Browse all insights
            </Link>
            <Link to="/features" className="border border-navy-900 text-navy-900 font-medium px-6 py-2.5 rounded hover:bg-navy-50 transition-colors">
              Explore Tactmark
            </Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <article className="max-w-2xl mx-auto px-6 pt-16 pb-4">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <Link to="/" className="hover:text-navy-700 hover:underline">Home</Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <Link to="/insights" className="hover:text-navy-700 hover:underline">Insights</Link>
        </nav>

        <p className="text-gray-400 text-xs mt-7 mb-2">
          {article.category || 'Guide'}
          {article.publish_date ? ` · ${formatDate(article.publish_date)}` : ''}
          {article.author ? ` · ${article.author}` : ''}
          {` · ${readingTime} min read`}
        </p>

        <h1 className="font-serif text-3xl sm:text-4xl text-navy-900 leading-tight mb-5">{article.title}</h1>

        {article.meta_description && (
          <p className="text-gray-600 text-lg leading-relaxed border-l-2 border-navy-200 pl-5 mb-10">
            {article.meta_description}
          </p>
        )}

        {article.sections.map((section, sectionIndex) => (
          <section key={`${section.heading}-${sectionIndex}`} className="mb-9">
            <h2 className="text-navy-900 font-semibold text-xl mb-3">{section.heading}</h2>
            <div className="space-y-4">
              {section.paragraphs.map((paragraph, paragraphIndex) => (
                <p key={paragraphIndex} className="text-gray-700 leading-relaxed">{paragraph}</p>
              ))}
            </div>
          </section>
        ))}

        <div className="bg-navy-50 border border-navy-100 rounded-2xl p-8 mt-12 text-center">
          <p className="text-navy-900 font-semibold mb-2">Turn match evidence into coaching priorities</p>
          <p className="text-gray-500 text-sm mb-5">
            Tactmark turns match evidence, coaching observations, and historical patterns into structured post-match intelligence and training priorities.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/login?mode=signup" className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-2.5 rounded transition-colors">
              Start free trial
            </Link>
            <Link to="/features" className="inline-block border border-navy-900 text-navy-900 font-medium px-6 py-2.5 rounded hover:bg-white transition-colors">
              See features
            </Link>
            <Link to="/pricing" className="inline-block border border-navy-900 text-navy-900 font-medium px-6 py-2.5 rounded hover:bg-white transition-colors">
              View pricing
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <aside className="max-w-2xl mx-auto px-6 py-16 border-t border-gray-200 mt-8" aria-label="Related insights">
          <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-6">More in {article.category || 'Insights'}</p>
          <div className="space-y-5">
            {related.map((item) => (
              <Link key={item.slug} to={`/insights/${item.slug}`} className="block group">
                <p className="text-navy-900 font-medium group-hover:text-navy-600 transition-colors">{item.title}</p>
                {item.meta_description && (
                  <p className="text-gray-500 text-sm mt-1 leading-relaxed">{item.meta_description}</p>
                )}
              </Link>
            ))}
          </div>
        </aside>
      )}

      <PublicFooter />
    </div>
  );
}
